import React, { useCallback, useEffect, useState } from 'react';
import {
  PhoneCall, X, Clock, CircleAlert, CalendarClock, Lock
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import RegistrarGestionModal from '../../components/RegistrarGestionModal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ToastProvider';
import { creditosAPI, cobranzaAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/Expediente.css';
import '../../styles/FormModal.css';
import '../../styles/PagosTab.css';
import '../../styles/CobranzaTab.css';

const moneda = formatoMoneda;

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso) return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

const fechaHora = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const TIPOS_GESTION = [
  { value: 'LLAMADA', label: 'Llamada' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'VISITA', label: 'Visita' },
  { value: 'MENSAJE', label: 'SMS / Mensaje' },
  { value: 'OTRO', label: 'Otro' }
];

/**
 * Pestaña Cobranza del expediente de crédito.
 * Muestra el historial operativo, compromisos de pago y situación de mora.
 */
function CobranzaTab({ credito }) {
  const toast = useToast();
  const { hasPermission, reviewMode } = useAuth();

  const [situacion, setSituacion] = useState(null);
  const [gestiones, setGestiones] = useState([]);
  const [promesas, setPromesas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [mostrarGestion, setMostrarGestion] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [s, g, p] = await Promise.all([
        creditosAPI.situacionCobranza(credito.id),
        creditosAPI.listarGestiones(credito.id),
        creditosAPI.listarPromesas(credito.id)
      ]);
      setSituacion(s.data);
      setGestiones(g.data?.gestiones || []);
      setPromesas(p.data?.promesas || []);
    } catch (e) {
      setError(describirError(e));
    } finally {
      setCargando(false);
    }
  }, [credito.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando) {
    return (
      <div className="loading" style={{ padding: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
        <Clock className="spin" size={18} />
        <span>Cargando expediente de cobranza…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="estado-vacio">
        <span className="estado-vacio-icono is-error"><CircleAlert size={20} strokeWidth={1.75} /></span>
        <p className="estado-vacio-texto">{error.mensaje}</p>
        <button className="btn btn-secondary" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const saldoActualNum = Number(credito?.saldoActual ?? situacion?.saldoExigible ?? 0);
  const saldoVencidoNum = Number(credito?.saldoVencidoReal ?? situacion?.saldoVencidoReal ?? situacion?.saldoCuotas ?? 0);
  const diasAtrasoNum = Number(credito?.diasAtraso ?? situacion?.diasAtraso ?? 0);
  const saldoMoratorioNum = Number(credito?.saldoMoratorios ?? situacion?.saldoMoratorios ?? 0);

  const handleCancelarPromesa = async (p) => {
    if (!p) return;
    const motivo = window.prompt('Ingrese el motivo de cancelación del compromiso de pago:');
    if (!motivo || !motivo.trim()) return;
    try {
      await cobranzaAPI.cancelarPromesa(p.id, motivo.trim());
      toast.exito('Compromiso cancelado correctamente.');
      await cargar();
    } catch (err) {
      toast.error(describirError(err).mensaje || 'Error al cancelar la promesa');
    }
  };

  const promesaVigente = promesas.find((p) => p.estado === 'VIGENTE');
  const promesaAfectadaReverso = promesas.find(
    (p) => p.estado === 'CERRADA' && p.motivoCierre === 'CUMPLIMIENTO' && (p.cumplimientoAfectadoPorReverso || p.displayStatus === 'CUMPLIMIENTO_AFECTADO_POR_REVERSO')
  );

  return (
    <div className="exp-panel pagos-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* 1. GRID FINANCIERO DE COBRANZA */}
      <div className="pagos-saldos-grid cobranza-situacion-grid">
        <div className="pagos-saldo-tile">
          <span className="pagos-resumen-etiqueta">SITUACIÓN</span>
          <span className="pagos-saldo-valor">
            <StatusBadge value={situacion?.situacion || credito?.situacion || 'VENCIDO'} />
          </span>
        </div>
        <div className={`pagos-saldo-tile ${diasAtrasoNum > 0 ? 'is-alerta' : ''}`}>
          <span className="pagos-resumen-etiqueta">DÍAS DE ATRASO</span>
          <span className="pagos-saldo-valor font-mono" style={{ color: diasAtrasoNum > 0 ? '#dc2626' : '#16a34a' }}>
            {diasAtrasoNum} d
          </span>
        </div>
        <div className="pagos-saldo-tile">
          <span className="pagos-resumen-etiqueta">SALDO VENCIDO REAL</span>
          <span className="pagos-saldo-valor font-mono text-danger font-bold">
            {moneda(saldoVencidoNum)}
          </span>
        </div>
        <div className="pagos-saldo-tile is-total">
          <span className="pagos-resumen-etiqueta">SALDO ACTUAL</span>
          <span className="pagos-saldo-valor font-mono text-teal">
            {moneda(saldoActualNum)}
          </span>
        </div>
      </div>

      {/* 2. DETALLE DE CUOTAS Y VENCIMIENTO */}
      <div className="pagos-resumen" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">CUOTA MÁS ANTIGUA</span>
          <span className="pagos-resumen-valor font-mono">
            {situacion?.cuotaMasAntiguaPendiente?.numeroPago ? `#${situacion.cuotaMasAntiguaPendiente.numeroPago}` : (credito?.cuotasVencidas ? `${credito.cuotasVencidas} vencida(s)` : '—')}
          </span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">FECHA EXIGIBLE</span>
          <span className="pagos-resumen-valor font-mono">
            {fechaCorta(situacion?.cuotaMasAntiguaPendiente?.fechaVencimiento || credito?.proximoVencimiento)}
          </span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">CUOTA EXIGIBLE PENDIENTE</span>
          <span className="pagos-resumen-valor font-mono font-semibold" style={{ color: '#1d4ed8' }}>
            {situacion?.cuotaMasAntiguaPendiente?.importePendiente != null ? moneda(situacion.cuotaMasAntiguaPendiente.importePendiente) : (credito?.importeProximoPago != null ? moneda(credito.importeProximoPago) : '—')}
          </span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">MORATORIO REAL</span>
          <span className="pagos-resumen-valor font-mono">
            {saldoMoratorioNum > 0 ? (
              <span className="text-danger font-bold">{moneda(saldoMoratorioNum)}</span>
            ) : (
              <span className="text-muted">$0.00</span>
            )}
          </span>
        </div>
      </div>

      {/* 3. CARD PROMESA VIGENTE (SI EXISTE) */}
      {promesaVigente && (
        <div className="exp-card" style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
          <div className="exp-card-header" style={{ borderBottom: '1px solid #bbf7d0', padding: '0.75rem 1rem' }}>
            <CalendarClock size={16} style={{ color: '#15803d' }} />
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#166534' }}>
              PROMESA DE PAGO VIGENTE
            </h3>
          </div>
          <div className="exp-card-body" style={{ padding: '0.85rem 1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', alignItems: 'center' }}>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>IMPORTE COMPROMETIDO</span>
                <span className="font-mono font-bold text-emerald-800" style={{ fontSize: '1.15rem' }}>
                  {moneda(promesaVigente.importePrometido)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>FECHA COMPROMISO</span>
                <span className="font-mono font-bold text-slate-800" style={{ fontSize: '0.95rem' }}>
                  {fechaCorta(promesaVigente.fechaPromesa)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>FECHA DE REGISTRO</span>
                <span className="text-xs text-slate-600 font-mono">
                  {fechaHora(promesaVigente.createdAt)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>REGISTRADO POR</span>
                <span className="text-xs text-slate-700 font-medium">
                  {promesaVigente.createdBy || 'Gestor'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={reviewMode || !hasPermission('PROMESAS_CANCELAR')}
                  title={
                    reviewMode
                      ? "Cancelación de promesas deshabilitada en Modo Revisión (READ_ONLY)"
                      : !hasPermission('PROMESAS_CANCELAR')
                      ? "Permiso insuficiente para cancelar promesas"
                      : "Cancelar promesa vigente"
                  }
                  onClick={() => handleCancelarPromesa(promesaVigente)}
                  style={{
                    opacity: (reviewMode || !hasPermission('PROMESAS_CANCELAR')) ? 0.65 : 1,
                    cursor: (reviewMode || !hasPermission('PROMESAS_CANCELAR')) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  {reviewMode ? <Lock size={12} /> : <X size={12} />}
                  <span>Cancelar promesa</span>
                </button>
              </div>
            </div>
            {promesaVigente.observaciones && (
              <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #bbf7d0', fontSize: '0.8rem', color: '#166534' }}>
                <strong>Observaciones:</strong> {promesaVigente.observaciones}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3B. CARD PROMESA CERRADA AFECTADA POR REVERSO */}
      {promesaAfectadaReverso && !promesaVigente && (
        <div className="exp-card" style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74' }}>
          <div className="exp-card-header" style={{ borderBottom: '1px solid #fed7aa', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CalendarClock size={16} style={{ color: '#c2410c' }} />
              <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: '700', color: '#9a3412' }}>
                PROMESA CERRADA · CUMPLIMIENTO AFECTADO POR REVERSO
              </h3>
            </div>
            <StatusBadge value="cumplimiento_afectado_por_reverso" />
          </div>
          <div className="exp-card-body" style={{ padding: '0.85rem 1rem' }}>
            <p style={{ margin: '0 0 0.65rem 0', fontSize: '0.8rem', color: '#c2410c', fontWeight: '500' }}>
              El pago que originó el cumplimiento de esta promesa fue reversado. La promesa permanece formalmente cerrada como evidencia histórica.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>IMPORTE PROMETIDO</span>
                <span className="font-mono font-bold text-slate-800" style={{ fontSize: '1rem' }}>
                  {moneda(promesaAfectadaReverso.importePrometido)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>ATRIBUCIÓN ACTIVA</span>
                <span className="font-mono font-bold text-danger" style={{ fontSize: '1rem' }}>
                  {moneda(promesaAfectadaReverso.importeAtribuidoActivo || 0)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>FECHA COMPROMISO</span>
                <span className="font-mono font-bold text-slate-800" style={{ fontSize: '0.9rem' }}>
                  {fechaCorta(promesaAfectadaReverso.fechaPromesa)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>FECHA DE CIERRE</span>
                <span className="text-xs text-slate-600 font-mono">
                  {fechaHora(promesaAfectadaReverso.cerradaEn)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted" style={{ display: 'block', fontWeight: '600' }}>MOTIVO CIERRE</span>
                <span className="text-xs font-semibold text-slate-700">
                  {promesaAfectadaReverso.motivoCierre || 'CUMPLIMIENTO'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. HISTORIAL DE GESTIONES */}
      <div className="exp-panel-head pagos-panel-head" style={{ marginTop: '0.5rem' }}>
        <div>
          <h3 style={{ fontSize: '1.05rem', fontWeight: '700' }}>HISTORIAL DE GESTIONES</h3>
          <p className="text-xs text-muted">Registro auditable de contactos, llamadas, visitas y acuerdos.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setMostrarGestion(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <PhoneCall size={14} />
          <span>Registrar gestión</span>
        </button>
      </div>

      {gestiones.length === 0 ? (
        <div className="estado-vacio" style={{ padding: '2rem 1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px dashed #cbd5e1' }}>
          <span className="estado-vacio-icono"><Clock size={22} strokeWidth={1.75} /></span>
          <p className="estado-vacio-titulo" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>
            Aún no existen gestiones registradas para este crédito.
          </p>
          <p className="estado-vacio-texto" style={{ fontSize: '0.8rem' }}>
            Las gestiones realizadas por el equipo de cobranza aparecerán ordenadas de la más reciente a la más antigua.
          </p>
        </div>
      ) : (
        <ol className="timeline">
          {gestiones.map((g) => (
            <li key={g.id} className="timeline-item">
              <span className="timeline-punto is-neutro">
                <PhoneCall size={14} strokeWidth={1.75} />
              </span>
              <div className="timeline-cuerpo">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <p className="timeline-fecha font-semibold text-slate-800" style={{ margin: 0 }}>
                    {fechaCorta(g.fechaGestion)} · {fechaHora(g.fechaGestion)}
                  </p>
                  <StatusBadge value={g.resultadoGestion} />
                </div>
                <p className="timeline-texto" style={{ marginTop: '0.25rem', fontWeight: '600', color: '#1e293b' }}>
                  {TIPOS_GESTION.find((t) => t.value === g.tipoGestion)?.label || g.tipoGestion}
                </p>
                {g.observaciones && <p className="timeline-detalle" style={{ color: '#334155' }}>{g.observaciones}</p>}
                {g.promesa && (
                  <p className="timeline-detalle" style={{ color: '#15803d', fontWeight: '600' }}>
                    Compromiso: {moneda(g.promesa.importePrometido)} para el {fechaCorta(g.promesa.fechaPromesa)}
                  </p>
                )}
                {g.proximaGestionEn && (
                  <p className="timeline-detalle text-xs text-muted">
                    Próxima acción agendada: {fechaCorta(g.proximaGestionEn)}
                  </p>
                )}
                <p className="timeline-actor text-xs text-muted" style={{ marginTop: '0.35rem' }}>
                  Gestionado por: <strong>{g.gestionadoPor}</strong>
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* 5. MODAL REGISTRAR GESTIÓN */}
      {mostrarGestion && (
        <RegistrarGestionModal
          credito={credito}
          saldoActual={saldoActualNum}
          saldoVencido={saldoVencidoNum}
          diasAtraso={diasAtrasoNum}
          hayPromesaVigente={!!promesaVigente}
          onClose={() => setMostrarGestion(false)}
          onRegistrada={async () => {
            setMostrarGestion(false);
            await cargar();
            toast.exito('Gestión registrada correctamente.');
          }}
        />
      )}
    </div>
  );
}

export default CobranzaTab;

